'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceFeature {
  icon: string;
  text: string;
}

interface ServiceCardProps {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  features: ServiceFeature[];
  pricing: {
    aed: string;
    usd: string;
  };
  onLearnMore: () => void;
}

const ServiceCard = ({
  title,
  description,
  image,
  imageAlt,
  features,
  pricing,
  onLearnMore,
}: ServiceCardProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleLearnMore = () => {
    if (isHydrated) {
      onLearnMore();
    }
  };

  const handleMouseEnter = () => {
    if (isHydrated) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (isHydrated) {
      setIsHovered(false);
    }
  };

  return (
    <div data-component-id="src/app/services/components/ServiceCard.tsx:61:4" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="61" data-component-end-line="111" data-component-file="ServiceCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-xl%20overflow-hidden%20border%20border-border%20hover%3Aborder-primary%20transition-all%20duration-300%20group%22%7D"
      className="bg-card rounded-xl overflow-hidden border border-border hover:border-primary transition-all duration-300 group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div data-component-id="src/app/services/components/ServiceCard.tsx:66:6" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="66" data-component-end-line="80" data-component-file="ServiceCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20h-64%20overflow-hidden%22%7D" className="relative h-64 overflow-hidden">
        <AppImage data-component-id="src/app/services/components/ServiceCard.tsx:67:8" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="67" data-component-end-line="73" data-component-file="ServiceCard.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22src%22%3A%22%5Bvar%3Aimage%5D%22%2C%22alt%22%3A%22%5Bvar%3AimageAlt%5D%22%7D"
          src={image}
          alt={imageAlt}
          className={`w-full h-full object-cover transition-transform duration-500 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}
        />
        <div data-component-id="src/app/services/components/ServiceCard.tsx:74:8" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="74" data-component-end-line="74" data-component-file="ServiceCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-gradient-to-t%20from-background%20via-background%2F50%20to-transparent%20opacity-80%22%7D" className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-80"></div>
        <div data-component-id="src/app/services/components/ServiceCard.tsx:75:8" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="75" data-component-end-line="79" data-component-file="ServiceCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-4%20left-4%20right-4%22%7D" className="absolute bottom-4 left-4 right-4">
          <h3 data-component-id="src/app/services/components/ServiceCard.tsx:76:10" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="76" data-component-end-line="78" data-component-file="ServiceCard.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-2xl%20font-outfit%20font-bold%20text-primary%20mb-2%22%7D" className="text-2xl font-outfit font-bold text-primary mb-2">
            {title}
          </h3>
        </div>
      </div>

      <div data-component-id="src/app/services/components/ServiceCard.tsx:82:6" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="82" data-component-end-line="110" data-component-file="ServiceCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%22%7D" className="p-6">
        <p data-component-id="src/app/services/components/ServiceCard.tsx:83:8" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="83" data-component-end-line="85" data-component-file="ServiceCard.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20text-sm%20leading-relaxed%20mb-6%22%7D" className="text-text-secondary text-sm leading-relaxed mb-6">
          {description}
        </p>

        <div data-component-id="src/app/services/components/ServiceCard.tsx:87:8" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="87" data-component-end-line="100" data-component-file="ServiceCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%20mb-6%22%7D" className="space-y-3 mb-6">
          {features.map((feature, index) => (
            <div data-component-id="src/app/services/components/ServiceCard.tsx:89:12" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="89" data-component-end-line="98" data-component-file="ServiceCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20space-x-3%22%7D" key={index} className="flex items-start space-x-3">
              <div data-component-id="src/app/services/components/ServiceCard.tsx:90:14" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="90" data-component-end-line="96" data-component-file="ServiceCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20w-5%20h-5%20rounded-full%20bg-primary%2F20%20flex%20items-center%20justify-center%20mt-0.5%22%7D" className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center mt-0.5">
                <Icon data-component-id="src/app/services/components/ServiceCard.tsx:91:16" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="91" data-component-end-line="95" data-component-file="ServiceCard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-primary%22%7D"
                  name={feature.icon as any}
                  size={12}
                  className="text-primary"
                />
              </div>
              <span data-component-id="src/app/services/components/ServiceCard.tsx:97:14" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="97" data-component-end-line="97" data-component-file="ServiceCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-foreground%22%7D" className="text-sm text-foreground">{feature.text}</span>
            </div>
          ))}
        </div>

        <div data-component-id="src/app/services/components/ServiceCard.tsx:102:8" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="102" data-component-end-line="109" data-component-file="ServiceCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20space-x-3%20mt-2%22%7D" className="flex space-x-3 mt-2">
          <Link data-component-id="src/app/services/components/ServiceCard.tsx:103:10" data-component-path="src/app/services/components/ServiceCard.tsx" data-component-line="103" data-component-end-line="108" data-component-file="ServiceCard.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fcontact%22%2C%22className%22%3A%22flex-1%20px-4%20py-3%20bg-primary%20text-primary-foreground%20font-outfit%20font-semibold%20text-sm%20rounded-lg%20hover%3Abg-primary%2F90%20transform%20hover%3A-translate-y-0.5%20transition-all%20duration-300%20shadow-lg%20hover%3Ashadow-primary%2F30%20text-center%22%2C%22textContent%22%3A%22Book%20Now%22%7D"
            href="/contact"
            className="flex-1 px-4 py-3 bg-primary text-primary-foreground font-outfit font-semibold text-sm rounded-lg hover:bg-primary/90 transform hover:-translate-y-0.5 transition-all duration-300 shadow-lg hover:shadow-primary/30 text-center"
          >
            Book Now
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;